import { Stack, useBreakpointValue } from "@chakra-ui/react";
import { SectionContentLayout } from "../../../components/utils";
import type { DealFinderTicker } from "../../../../../api/tickers/models/DealFinderTicker";
import { useCollectionKey, useDealFinderTickers } from "../../../../../hooks";
import React from "react";
import SectionInvestmentSummary from "../../CommonComponents/Sections/SectionContents/SectionInvestmentSummary";
import SectionInvestorReadiness from "../../CommonComponents/Sections/SectionContents/SectionInvestorReadiness";
import SectionEquityRounds from "../../CommonComponents/Sections/SectionContents/SectionEquityRounds";
import SectionDebtFinancing from "../../CommonComponents/Sections/SectionContents/SectionDebtFinancing";
import SectionCapTable from "../../CommonComponents/Sections/SectionContents/SectionCapTable";
import SectionStockOptions from "../../CommonComponents/Sections/SectionContents/SectionStockOptions";

interface Props {
  collectionId: string;
  isLoading?: boolean;
}

export const CompanyDetailViewInvestmentTab: React.FC<Props> = ({ collectionId, isLoading = false }) => {
  const [tickerData, setTickerData] = React.useState<DealFinderTicker | null>(null);

  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });

  const { getDealFinderTickers } = useDealFinderTickers();

  const ticker = useCollectionKey(collectionId, "ticker");
  const exchange = useCollectionKey(collectionId, "stockExchange");

  // Fetch ticker data
  React.useEffect(() => {
    const fetchTickerData = async () => {
      if (ticker && exchange) {
        try {
          const data = await getDealFinderTickers({
            symbolWithExchange: `${exchange}:${ticker}`,
          });
          setTickerData(data.length > 0 ? data[0] : null);
        } catch (error) {
          console.error("Error fetching ticker data:", error);
          setTickerData(null);
        }
      }
    };

    fetchTickerData();
  }, [ticker, exchange, getDealFinderTickers]);

  // Mock investment summary data (would come from ticker data in real implementation)
  const investmentSummaryData = React.useMemo(() => {
    if (tickerData) {
      return {
        totalTransactions: 68,
        runningTotalRaised: "$7.4M",
        mostRecentValuation: "$32.6M",
        totalShares: "18,390,068",
        lastSharePrice: "$1.77",
        totalInvestors: 58,
      };
    }
    // Mock data when no real data available
    return {
      totalTransactions: 68,
      runningTotalRaised: "$7.4M",
      mostRecentValuation: "$32.6M",
      totalShares: "18,390,068",
      lastSharePrice: "$1.77",
      totalInvestors: 58,
    };
  }, [tickerData]);

  const equityRoundsData = React.useMemo(() => {
    if (tickerData) {
      return [
        { roundId: "CDConvert01", stage: "Other", endDate: 1735603200, totalRaised: "$1,631,658.83" },
        { roundId: "SeriesB", stage: "Series B", endDate: 1678060800, totalRaised: "$386,212.77" },
        { roundId: "Buyback01", stage: "Buyback", endDate: 1643587200, totalRaised: "$-242,756.76" },
        { roundId: "SeriesA", stage: "Series A", endDate: 1632873600, totalRaised: "$3,816,987.04" },
        { roundId: "Seed02B", stage: "Seed B", endDate: 1593475200, totalRaised: "$1,399,776.72" },
        { roundId: "Seed01A", stage: "Seed A", endDate: 1551139200, totalRaised: "$399,999.90" },
        { roundId: "Founding01", stage: "Founding", endDate: 1524096000, totalRaised: "$10.00" },
      ];
    }
    // Mock data when no real data available
    return [
      { roundId: "CDConvert01", stage: "Other", endDate: 1735603200, totalRaised: "$1,631,658.83" },
      { roundId: "SeriesB", stage: "Series B", endDate: 1678060800, totalRaised: "$386,212.77" },
      { roundId: "Buyback01", stage: "Buyback", endDate: 1643587200, totalRaised: "$-242,756.76" },
      { roundId: "SeriesA", stage: "Series A", endDate: 1632873600, totalRaised: "$3,816,987.04" },
      { roundId: "Seed02B", stage: "Seed B", endDate: 1593475200, totalRaised: "$1,399,776.72" },
      { roundId: "Seed01A", stage: "Seed A", endDate: 1551139200, totalRaised: "$399,999.90" },
      { roundId: "Founding01", stage: "Founding", endDate: 1524096000, totalRaised: "$10.00" },
    ];
  }, [tickerData]);

  const debtRoundsData = React.useMemo(() => {
    if (tickerData) {
      return [
        { roundId: "AllumiqsCD01", stage: "Series A1", endDate: 1735603200, totalRaised: "$1,457,000.00" },
        { roundId: "SeriesB", stage: "Series B", endDate: 1678060800, totalRaised: "$386,212.77" },
      ];
    }
    // Mock data when no real data available
    return [
      { roundId: "AllumiqsCD01", stage: "Series A1", endDate: 1735603200, totalRaised: "$1,457,000.00" },
      { roundId: "SeriesB", stage: "Series B", endDate: 1678060800, totalRaised: "$386,212.77" },
    ];
  }, [tickerData]);

  const capTableData = React.useMemo(() => {
    if (tickerData) {
      return [
        { fullName: "Bianca A. Lang", totalShares: 14500, ownership: 0.1 },
        { fullName: "Hugo Gagnon", totalShares: 39681, ownership: 0.2 },
        { fullName: "Peter G Hickey", totalShares: 242062, ownership: 1.3 },
        { fullName: "Nextwave Corporation", totalShares: 1619208, ownership: 8.8 },
        { fullName: "Neil Smith", totalShares: 1779458, ownership: 9.7 },
        { fullName: "Karine L'Eriger", totalShares: 39681, ownership: 0.2 },
        { fullName: "Alan Doucette", totalShares: 943627, ownership: 5.1 },
        { fullName: "Richard Balys", totalShares: 745022, ownership: 4.2 },
      ];
    }
    // Mock data when no real data available
    return [
      { fullName: "Bianca A. Lang", totalShares: 14500, ownership: 0.1 },
      { fullName: "Hugo Gagnon", totalShares: 39681, ownership: 0.2 },
      { fullName: "Peter G Hickey", totalShares: 242062, ownership: 1.3 },
      { fullName: "Nextwave Corporation", totalShares: 1619208, ownership: 8.8 },
      { fullName: "Neil Smith", totalShares: 1779458, ownership: 9.7 },
      { fullName: "Karine L'Eriger", totalShares: 39681, ownership: 0.2 },
      { fullName: "Alan Doucette", totalShares: 943627, ownership: 5.1 },
      { fullName: "Richard Balys", totalShares: 745022, ownership: 4.2 },
    ];
  }, [tickerData]);

  const stockOptionData = React.useMemo(() => {
    if (tickerData) {
      return [{ optionPoolId: "Seed A", employeeCount: 24, endDate: 1735603200, totalOptions: 50000 }];
    }
    // Mock data when no real data available
    return [{ optionPoolId: "Seed A", employeeCount: 24, endDate: 1735603200, totalOptions: 50000 }];
  }, [tickerData]);

  // Mock data for sections that can't be populated with real data
  const mockInvestorReadinessScore = 34;

  console.log("Is page loading: ", isLoading);

  return (
    <SectionContentLayout useGrid className="ch-company-dashboard-sections">
      {/* Left Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionInvestorReadiness score={mockInvestorReadinessScore} showRaiseCapitalRound showPerformValuation />
        <SectionInvestmentSummary investmentSummaryData={investmentSummaryData} />
        <SectionCapTable shareholders={capTableData} />
      </Stack>

      {/* Right Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionEquityRounds rounds={equityRoundsData} />
        <SectionDebtFinancing rounds={debtRoundsData} />
        <SectionStockOptions stockOptionPools={stockOptionData} />
      </Stack>
    </SectionContentLayout>
  );
};
